import React, { useState } from 'react'
import { Tooltip } from '@mui/material'
import {
  CheckCircleOutlined,
  ChevronLeftOutlined,
  ChevronRightOutlined,
  ErrorOutlineOutlined,
  GridViewOutlined,
  PauseCircleOutlined,
  PowerSettingsNewOutlined
} from '@mui/icons-material'
import { useSmartTranslation } from '../generic/hooks/useSmartTranslation'
import { ALL_GROUPS_ID, NO_GROUP_ID, QUICK_FILTERS } from './plantMonitoringUtils'

/**
 * DeviceGroupNavigator — the Stitch dashboard's left sidebar (stitch-dashboard.html), 1:1:
 * a "CELL NAVIGATION" section (one entry per group — bold name, uppercase micro-meta,
 * selected entry in the primary container with its left accent border, alarmed deviceGroups
 * with the pulsing red dot and error-tinted name) and a "QUICK FILTERS" section
 * (icon + label + integrated count badge, mockup's "All Running 18 / Faulted Only 02
 * / Idle 04"). Also hosts the quick filters now — in the mockup they live in this
 * same sidebar, not above the grid.
 *
 * Kept from previous PO feedback: the whole sidebar still collapses to a narrow rail
 * (deviceGroups as clickable initial-squares, filters as icon buttons) so the card grid can
 * take the full width. Behaviour unchanged (S7/S8 group navigation, S14-S19 filters):
 * fully controlled, selection elevated to the owner, 100% client-side.
 */
export default function DeviceGroupNavigator ({
  deviceGroups = [],
  selectedGroupId,
  onSelect,
  activeFilter = QUICK_FILTERS.ALL,
  onSelectFilter = () => {},
  counts = {}
}) {
  const { t_ } = useSmartTranslation()
  // Collapsed by default (PO): the rail's compact icons/pills are enough to
  // navigate, and it leaves the card grid at full width until expanded.
  const [expanded, setExpanded] = useState(false)

  const items = [
    { id: ALL_GROUPS_ID, name: t_('All groups') },
    ...deviceGroups.filter((group) => group.id !== NO_GROUP_ID),
    ...deviceGroups.filter((group) => group.id === NO_GROUP_ID).map((group) => ({ ...group, name: t_('No group') }))
  ]

  // Filter labels are the REAL device state literals — OFF(0), IDLE(1),
  // WORKING(2), ERROR (PO directive) — matching exactly what the status chips show.
  const filters = [
    { key: QUICK_FILTERS.ALL, label: t_('All'), icon: GridViewOutlined, iconColor: 'var(--mcx-on-variant)' },
    { key: QUICK_FILTERS.WORKING, label: 'WORKING', icon: CheckCircleOutlined, iconColor: 'var(--mcx-primary)' },
    { key: QUICK_FILTERS.IDLE, label: 'IDLE', icon: PauseCircleOutlined, iconColor: 'var(--mcx-idle)' },
    { key: QUICK_FILTERS.ERROR, label: 'ERROR', icon: ErrorOutlineOutlined, iconColor: 'var(--mcx-error)' },
    { key: QUICK_FILTERS.OFF, label: 'OFF', icon: PowerSettingsNewOutlined, iconColor: 'var(--mcx-on-variant)' }
  ]

  const initials = (name) => String(name ?? '').trim().slice(0, 2).toUpperCase()

  return (
    <aside
      data-testid='group-navigator'
      data-expanded={expanded}
      className={`mcx-sidebar${expanded ? '' : ' mcx-sidebar--collapsed'}`}
    >
      <div className='mcx-sidebar__section' style={{ display: 'flex', justifyContent: expanded ? 'flex-end' : 'center' }}>
        <Tooltip title={expanded ? '' : t_('Groups')}>
          <button
            type='button'
            data-testid='group-navigator-toggle'
            className='mcx-iconbtn'
            onClick={() => setExpanded((current) => !current)}
          >
            {expanded ? <ChevronLeftOutlined fontSize='small' /> : <ChevronRightOutlined fontSize='small' />}
          </button>
        </Tooltip>
      </div>

      <div className='mcx-sidebar__section'>
        {expanded && <p className='mcx-eyebrow mcx-sidebar__title'>{t_('Groups')}</p>}
        {items.map((group) => {
          const selected = String(group.id) === String(selectedGroupId)
          const alarmed = group.hasAlarm === true
          if (!expanded) {
            return (
              <Tooltip key={group.id} title={group.name} placement='right'>
                <button
                  type='button'
                  data-testid={`group-item-${group.id}`}
                  className={[
                    'mcx-rail-item',
                    selected ? 'mcx-rail-item--selected' : '',
                    alarmed ? 'mcx-rail-item--alarm' : ''
                  ].filter(Boolean).join(' ')}
                  onClick={() => onSelect(group.id)}
                >
                  {initials(group.name)}
                </button>
              </Tooltip>
            )
          }
          return (
            <button
              key={group.id}
              type='button'
              data-testid={`group-item-${group.id}`}
              className={[
                'mcx-group-item',
                selected ? 'mcx-group-item--selected' : '',
                alarmed ? 'mcx-group-item--alarm' : ''
              ].filter(Boolean).join(' ')}
              onClick={() => onSelect(group.id)}
            >
              <span style={{ minWidth: 0 }}>
                <span className='mcx-group-item__name' style={{ display: 'block' }}>{group.name}</span>
                {group.deviceCount !== undefined && (
                  <span className='mcx-group-item__meta'>{group.deviceCount} · {group.groupType?.name ?? ''}</span>
                )}
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                {alarmed && <span className='mcx-dot mcx-pulse' style={{ width: 8, height: 8, background: 'var(--mcx-error)' }} />}
                <ChevronRightOutlined sx={{ fontSize: 18, opacity: 0.7 }} />
              </span>
            </button>
          )
        })}
      </div>

      <div className='mcx-sidebar__section'>
        {expanded && <p className='mcx-eyebrow mcx-sidebar__title'>{t_('Filter')}</p>}
        {filters.map((filter) => {
          const Icon = filter.icon
          const active = activeFilter === filter.key
          const count = counts[filter.key]
          if (!expanded) {
            return (
              <Tooltip key={filter.key} title={filter.label} placement='right'>
                <button
                  type='button'
                  data-testid={`quick-filter-${filter.key}`}
                  data-active={String(active)}
                  className={`mcx-rail-item${active ? ' mcx-rail-item--selected' : ''}`}
                  onClick={() => onSelectFilter(filter.key)}
                >
                  <Icon sx={{ fontSize: 20, color: active ? 'inherit' : filter.iconColor }} />
                </button>
              </Tooltip>
            )
          }
          return (
            <button
              key={filter.key}
              type='button'
              data-testid={`quick-filter-${filter.key}`}
              data-active={String(active)}
              className={`mcx-filter-item${active ? ' mcx-filter-item--active' : ''}`}
              onClick={() => onSelectFilter(filter.key)}
            >
              <span className='mcx-filter-item__label'>
                <Icon sx={{ fontSize: 20, color: filter.iconColor }} />
                <span className='mcx-label-caps'>{filter.label}</span>
              </span>
              {count !== undefined && (
                <span className={`mcx-filter-item__count${filter.key === QUICK_FILTERS.ERROR && count > 0 ? ' mcx-filter-item__count--error' : ''}`}>
                  {String(count).padStart(2, '0')}
                </span>
              )}
            </button>
          )
        })}
      </div>
    </aside>
  )
}
